import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { useNavigate } from 'react-router-dom';
import './tailwind.css';
import { FaEdit, FaTrash, FaKey, FaCheck, FaTimes, FaSpinner } from 'react-icons/fa'; // Icone

export default function GestisciAgenzie() {
  const [agenzieList, setAgenzieList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const navigate = useNavigate();

  // Stato per la modale di conferma/input password
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  // Modificato: onConfirmAction ora sarà null se non c'è una callback da eseguire
  const [onConfirmAction, setOnConfirmAction] = useState(null); 
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [targetUserIdForPasswordReset, setTargetUserIdForPasswordReset] = useState(null);
  const SUPABASE_FUNCTIONS_BASE_URL = 'https://zwgpmobugscpdcavajbu.supabase.co'; // Assicurati che sia l'URL corretto per le tue Edge Functions

  // Stato per la modifica in linea
  const [editingId, setEditingId] = useState(null); // Sarà l'userId che stiamo modificando
  const [editedData, setEditedData] = useState({}); // Contiene i dati modificati di agenzia e utente

  // Funzioni per la modale di conferma personalizzata
  const openConfirmModal = useCallback((title, message, confirmCallback, showPassword = false, targetUserId = null) => {
    setModalTitle(title);
    setModalMessage(message);
    // Imposta direttamente la callback. Se confirmCallback è null, onConfirmAction sarà null.
    setOnConfirmAction(() => confirmCallback); 
    setShowPasswordInput(showPassword);
    setTargetUserIdForPasswordReset(targetUserId);
    setTempPassword(''); // Resetta la password temporanea
    setShowConfirmModal(true);
  }, []);

  const closeConfirmModal = useCallback(() => {
    setShowConfirmModal(false);
    setModalTitle('');
    setModalMessage('');
    // Cruciale: resetta questo a null quando chiudi, per garantire "OK" al prossimo openConfirmModal senza callback
    setOnConfirmAction(null); 
    setShowPasswordInput(false);
    setTargetUserIdForPasswordReset(null);
    setTempPassword('');
  }, []);

  const handleConfirm = () => {
    if (onConfirmAction) {
      // Passa il valore corrente dello stato tempPassword alla funzione di azione
      // SOLO SE l'input password è stato mostrato nella modale
      if (showPasswordInput) {
        onConfirmAction(tempPassword);
      } else {
        onConfirmAction();
      }
    }
  };

  // Funzione per caricare le agenzie e i relativi utenti
  const loadAgenzie = useCallback(async () => {
    setLoading(true);
    try {
      console.log('Caricamento utenti con ruolo "responsabile" e relative sedi...');

      // 1. Prendi tutti gli utenti con ruolo 'agenzia'
      const { data: usersData, error: usersError } = await supabase
        .from('utenti')
        .select('id, nome, cognome, email, agenzia_id')
        .eq('ruolo', 'agenzia');

      if (usersError) throw usersError;

      // 2. Estrai gli ID delle agenzie da caricare
      const agencyIds = usersData.map(user => user.agenzia_id).filter(Boolean);

      // 3. Carica tutte le agenzie relative (solo quelle a cui è associato almeno un utente 'agenzia')
      let agenzieMap = {};
      if (agencyIds.length > 0) { // Evita query vuote
        const { data: agenzieData, error: agenzieError } = await supabase
          .from('agenzie')
          .select('id, nome')
          .in('id', agencyIds);

        if (agenzieError) throw agenzieError;

        // 4. Mappa le agenzie per ID per accesso veloce
        agenzieMap = agenzieData.reduce((acc, agency) => {
          acc[agency.id] = agency;
          return acc;
        }, {});
      }

      // 5. Crea l’elenco finale: ogni riga rappresenta un utente con il suo ruolo "agenzia" e i dettagli dell'agenzia associata
      const combinedData = usersData.map(user => ({
        // Dati dell'utente
        userId: user.id,
        nomeUtente: user.nome,
        cognomeUtente: user.cognome,
        emailUtente: user.email,
        // Dati dell'agenzia associata
        agenziaId: user.agenzia_id, // L'ID dell'agenzia a cui l'utente è associato
        nomeAgenzia: agenzieMap[user.agenzia_id]?.nome || 'N/D', // Prende il nome dell'agenzia dalla mappa
      }));

      setAgenzieList(combinedData);
      console.log('Utenti + agenzie:', combinedData);

    } catch (error) {
      console.error('Errore nel caricamento:', error.message);
      // Errore nel caricamento: mostra solo "OK"
      openConfirmModal('Errore', `Errore nel caricamento sedi: ${error.message}`, null);
      setAgenzieList([]);
    } finally {
      setLoading(false);
    }
  }, [openConfirmModal]);

  useEffect(() => {
    const fetchUserRole = async () => {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;

      if (sessionError || !userId) {
        console.error('No user session or error:', sessionError?.message);
        navigate('/');
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('utenti')
        .select('ruolo')
        .eq('id', userId)
        .single();

      if (profileError || !profile) {
        console.error('Error loading user profile:', profileError?.message);
        navigate('/');
        return;
      }

      // Solo i broker possono accedere a questa pagina
      if (profile.ruolo !== 'broker') {
        console.warn(`User with role ${profile.ruolo} attempted to access GestisciAgenzie.`);
        navigate('/dashboard/broker'); // Reindirizza alla dashboard del broker o a una pagina di errore
        return;
      }
      setUserRole(profile.ruolo);
      loadAgenzie(); // Carica le agenzie solo dopo aver confermato il ruolo
    };

    fetchUserRole();

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        navigate('/');
      }
    });

    return () => {
      authListener?.data?.unsubscribe();
    };
  }, [navigate, loadAgenzie]);

  const handleEdit = (userData) => {
    // userData è l'oggetto come definito in combinedData
    setEditingId(userData.userId); // Usiamo userId come ID per la riga in modifica
    setEditedData({
      userId: userData.userId,
      nomeUtente: userData.nomeUtente,
      cognomeUtente: userData.cognomeUtente,
      emailUtente: userData.emailUtente,
      agenziaId: userData.agenziaId,
      nomeAgenzia: userData.nomeAgenzia,
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditedData({});
  };

  const handleChangeEdit = (e) => {
    const { name, value } = e.target;
    setEditedData({ ...editedData, [name]: value });
  };

  const handleSave = async () => {
  setLoading(true);
  try {
    if (!editedData.nomeAgenzia || !editedData.nomeUtente || !editedData.cognomeUtente || !editedData.emailUtente) {
      openConfirmModal('Errore di Validazione', 'Tutti i campi sono obbligatori.', null);
      setLoading(false);
      return;
    }

    // Recupera la sessione per il token di autenticazione
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;

    if (!accessToken) {
      throw new Error('Access token non disponibile. Effettuare nuovamente il login.');
    }

    // Chiamata all'Edge Function per aggiornare agenzia e utente (inclusa l'email)
    const response = await fetch(`${SUPABASE_FUNCTIONS_BASE_URL}/functions/v1/update-user-profile`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`, // Passa il token dell'utente broker loggato
      },
      body: JSON.stringify({
        userId: editedData.userId,
        nomeUtente: editedData.nomeUtente,
        cognomeUtente: editedData.cognomeUtente,
        emailUtente: editedData.emailUtente,
        agenziaId: editedData.agenziaId,
        nomeAgenzia: editedData.nomeAgenzia, // Passiamo anche il nome agenzia per l'Edge Function
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Errore durante l\'aggiornamento.');
    }

    setEditingId(null);
    setEditedData({});
    await loadAgenzie(); // Ricarica i dati aggiornati

    openConfirmModal('Successo!', 'Sede e responsabile aggiornati con successo!', null);

  } catch (error) {
    console.error('Errore nel salvataggio:', error.message);
    openConfirmModal('Errore', `Si è verificato un errore nel salvataggio: ${error.message}.`, null);
  } finally {
    setLoading(false);
  }
};

  const handleDeleteAgency = async (agencyId, userId, agencyName) => {
    // Questa è una conferma "Sì/No", quindi passiamo la funzione async come confirmCallback
    openConfirmModal(
      'Conferma Eliminazione',
      `Sei sicuro di voler eliminare la sede "${agencyName}" e l'utente associato? Questa azione è irreversibile.`,
      async () => { // Questa è la confirmCallback che viene eseguita se l'utente clicca "Sì"
        setLoading(true);
        try {
            // Ottieni l'ID dell'utente loggato (il broker)
            const { data: sessionData } = await supabase.auth.getSession();
            const adminUserId = sessionData?.session?.user?.id || null;

            if (!adminUserId) {
                throw new Error('Impossibile recuperare l\'ID dell\'amministratore loggato.');
            }

            // Chiamata all'Edge Function per eliminare agenzia e utente
            const response = await fetch(`${SUPABASE_FUNCTIONS_BASE_URL}/functions/v1/delete-agency`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionData?.session?.access_token || 'anon'}`
              },
              body: JSON.stringify({ agencyId: agencyId, userIdToDelete: userId, adminUserId: adminUserId }), // Passa agencyId e userIdToDelete
            });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || 'Errore durante l\'eliminazione.');
          }

          // Dopo l'eliminazione riuscita, mostra un messaggio di successo con solo "OK"
          // Passiamo null come callback per far apparire solo il pulsante "OK".
          // La ricarica dei dati avviene dopo la chiusura della modale.
          openConfirmModal('Successo!', `Sede "${agencyName}" eliminata con successo!`, null); // Modificato qui
          loadAgenzie(); // Ricarica i dati dopo la chiusura della modale di successo
        } catch (error) {
          console.error('Errore durante l\'eliminazione della sede:', error.message);
          // Errore durante l'eliminazione: mostra solo "OK". Passiamo null per confirmCallback.
          openConfirmModal('Errore', `Errore nell'eliminazione: ${error.message}`, null);
        } finally {
          setLoading(false);
        }
      }
    );
  };

  const handleForcePasswordReset = (userId, userEmail) => {
    openConfirmModal(
      'Forza Nuova Password',
      `Inserisci la nuova password per la Sede con responsabile ${userEmail}.`,
      async (passwordFromModal) => { // La password aggiornata viene passata qui
        setLoading(true);
        try {
          // Log per debugging: mostra il valore di passwordFromModal
          console.log('Valore di passwordFromModal ricevuto:', passwordFromModal);

          const passwordToUse = passwordFromModal.trim(); 

          // Log per debugging: mostra il valore dopo il trim
          console.log('Valore di passwordToUse (dopo trim) nel callback:', passwordToUse);

          if (!passwordToUse || passwordToUse.length < 6) {
            openConfirmModal('Errore', 'La password deve essere di almeno 6 caratteri.', null);
            setLoading(false);
            return; // Questo return blocca l'esecuzione se la validazione fallisce
          }

          const { data: sessionData } = await supabase.auth.getSession();
          const accessToken = sessionData?.session?.access_token || 'anon';

          console.log(`Tentativo di chiamare la funzione reset-user-password per userId: ${userId}, URL: ${SUPABASE_FUNCTIONS_BASE_URL}/functions/v1/reset-user-password`);
          console.log(`Payload inviato:`, { targetUserId: userId, newPassword: passwordToUse });
          console.log(`Token di Autorizzazione (primi 10 char): ${accessToken.substring(0, 10)}...`);

          const response = await fetch(`${SUPABASE_FUNCTIONS_BASE_URL}/functions/v1/reset-user-password`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({ targetUserId: userId, newPassword: passwordToUse }),
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || 'Errore durante il reset della password.');
          }

          // Successo: mostra solo "OK". La callback chiude la modale.
          openConfirmModal('Successo!', 'Password aggiornata correttamente!', null); // Modificato qui
        } catch (error) {
          console.error('Errore durante il reset della password:', error.message);
          openConfirmModal('Errore', `Errore nel reset password: ${error.message}`, null);
        } finally {
          setLoading(false);
        }
      },
      true, // Questo abilita correttamente l'input della password nella modale
      userId // Passa l'ID utente target
    );
  };

  if (userRole === null || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 text-slate-800">
        <FaSpinner className="animate-spin text-4xl text-mioblu mr-3" />
        <p className="text-xl font-semibold">Caricamento sedi...</p>
      </div>
    );
  }

  // Se l'utente non è un broker, non dovrebbe vedere nulla (o essere reindirizzato altrove da useEffect)
  if (userRole !== 'broker') {
    return null; // O un messaggio di accesso negato
  }

  return (
    <div className="p-4 md:p-8 bg-gray-50 min-h-screen">
      <h1 className="text-2xl md:text-3xl font-bold uppercase mb-4 text-center text-mioblu">Gestione Sedi</h1>

      <div className="bg-white p-6 rounded-lg shadow-md mb-8 overflow-x-auto">
        <table className="min-w-full table-fixed border-collapse">
          <thead>
            <tr className="bg-gray-100 text-mioblu uppercase text-sm leading-normal">
              <th className="py-3 px-6 text-left w-1/5">Sede</th>
              <th className="py-3 px-6 text-left w-1/5">Nome Resp.</th>
              <th className="py-3 px-6 text-left w-1/5">Cognome Resp.</th>
              <th className="py-3 px-6 text-left w-1/5">Email Resp.</th>
              <th className="py-3 px-6 text-center w-1/5">Azioni</th>
            </tr>
          </thead>
          <tbody className="text-gray-600 text-sm font-light">
            {agenzieList.length > 0 ? (
              agenzieList.map((item) => ( // Ho cambiato il nome della variabile da 'agency' a 'item' per chiarezza
                <tr key={item.userId} className="border-b border-gray-200 hover:bg-gray-50">
                  {editingId === item.userId ? ( // Confronta con userId per la modifica
                    <>
                      {/* Cella per Nome Agenzia (modificabile) */}
                      <td className="py-3 px-2 text-left align-middle">
                        <input
                          type="text"
                          name="nomeAgenzia"
                          value={editedData.nomeAgenzia}
                          onChange={handleChangeEdit}
                          className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                        />
                      </td>
                      {/* Cella per Nome Utente (modificabile) */}
                      <td className="py-3 px-2 text-left align-middle">
                        <input
                          type="text"
                          name="nomeUtente"
                          value={editedData.nomeUtente}
                          onChange={handleChangeEdit}
                          className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                        />
                      </td>
                      {/* Cella per Cognome Utente (modificabile) */}
                      <td className="py-3 px-2 text-left align-middle">
                        <input
                          type="text"
                          name="cognomeUtente"
                          value={editedData.cognomeUtente}
                          onChange={handleChangeEdit}
                          className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                        />
                      </td>
                      {/* Cella per Email Utente (modificabile) */}
                      <td className="py-3 px-2 text-left align-middle">
                        <input
                          type="email"
                          name="emailUtente"
                          value={editedData.emailUtente}
                          onChange={handleChangeEdit}
                          className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                        />
                      </td>
                      {/* Cella Azioni (Salva/Annulla) */}
                      <td className="py-3 px-2 text-center align-middle">
                        <div className="flex justify-center items-center gap-2">
                          <button
                            className="w-9 h-9 flex items-center justify-center p-0 text-base bg-green-500 text-white rounded transition-colors duration-200 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500"
                            onClick={handleSave}
                            title="Salva"
                            disabled={loading}
                          >
                            <FaCheck />
                          </button>
                          <button
                            className="w-9 h-9 flex items-center justify-center p-0 text-base bg-gray-500 text-white rounded transition-colors duration-200 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                            onClick={handleCancel}
                            title="Annulla"
                            disabled={loading}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      {/* Visualizzazione normale */}
                      <td className="py-3 px-6 text-left align-middle font-semibold">{item.nomeAgenzia}</td>
                      <td className="py-3 px-6 text-left align-middle">{item.nomeUtente || '-'}</td>
                      <td className="py-3 px-6 text-left align-middle">{item.cognomeUtente || '-'}</td>
                      <td className="py-3 px-6 text-left align-middle">{item.emailUtente || '-'}</td>
                      <td className="py-3 px-6 text-center align-middle">
                        <div className="flex justify-center items-center gap-2 min-h-12">
                          <button
                            className="w-9 h-9 flex items-center justify-center p-0 text-base bg-blue-500 text-white rounded transition-colors duration-200 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onClick={() => handleEdit(item)}
                            title="Modifica"
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="w-9 h-9 flex items-center justify-center p-0 text-base bg-red-500 text-white rounded transition-colors duration-200 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                            onClick={() => handleDeleteAgency(item.agenziaId, item.userId, item.nomeAgenzia)}
                            title="Elimina"
                          >
                            <FaTrash />
                          </button>
                          {item.userId && (
                            <button
                              className="w-9 h-9 flex items-center justify-center p-0 text-base bg-purple-500 text-white rounded transition-colors duration-200 hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
                              onClick={() => handleForcePasswordReset(item.userId, item.emailUtente)}
                              title="Forza Nuova Password"
                            >
                              <FaKey />
                            </button>
                          )}
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="py-4 text-center text-gray-500">Nessuna sede trovata.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modale di Conferma/Input Password Personalizzata */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full text-center">
            <h3 className="text-xl font-bold mb-4 text-gray-800">{modalTitle}</h3>
            <p className="text-gray-700 mb-6">{modalMessage}</p>

            {showPasswordInput && (
              <div className="mb-6">
                <label htmlFor="tempPassword" className="sr-only">Nuova Password Temporanea</label>
                <input
                  type="password"
                  id="tempPassword"
                  value={tempPassword}
                  onChange={(e) => setTempPassword(e.target.value)}
                  placeholder="Nuova password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Minimo 6 caratteri.</p>
              </div>
            )}

            <div className="flex justify-center gap-4">
              {/* Pulsante "Sì" (o azione di conferma) - Mostrato solo se c'è una onConfirmAction */}
              {onConfirmAction && (
                <button
                  className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  onClick={handleConfirm}
                  disabled={loading || (showPasswordInput && tempPassword.length < 6)} 
                >
                  {loading ? <FaSpinner className="animate-spin" /> : 'Sì'}
                </button>
              )}
              {/* Pulsante "No" o "OK" */}
              <button
                className="px-6 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors"
                // Se c'è una onConfirmAction, questo è il "No", altrimenti è l'"OK" che chiude il modale
                onClick={closeConfirmModal} // Questo pulsante chiama sempre closeConfirmModal
                disabled={loading}
              >
                {onConfirmAction ? 'No' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
